"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.exportedForTesting = exports.token = exports.getNewAccessToken = exports.getCredentialsFromSSM = exports.getParameters = void 0;
/* eslint-disable @typescript-eslint/explicit-function-return-type */
const axios_1 = __importDefault(require("axios"));
const client_ssm_1 = require("@aws-sdk/client-ssm");
const jsonwebtoken_1 = __importDefault(require("jsonwebtoken"));
const AssertHelper_1 = require("./AssertHelper");
/**
 * The status of a token's expiration.
 *
 * This enum is returned {@link getTokenExpirationStatus} to be used by the {@link token} function to determine if token should be refreshed.
 * - EXPIRED: The token has expired.
 * - EXPIRING_SOON: The token is expiring within @see {@link EXPIRING_SOON_THRESHOLD}.
 * - NOT_EXPIRED: The token is not expired and is not expiring soon.
 */
var TokenExpirationStatus;
(function (TokenExpirationStatus) {
    TokenExpirationStatus["EXPIRED"] = "EXPIRED";
    TokenExpirationStatus["EXPIRING_SOON"] = "EXPIRING_SOON";
    TokenExpirationStatus["NOT_EXPIRED"] = "NOT_EXPIRED";
})(TokenExpirationStatus || (TokenExpirationStatus = {}));
const getParameters = async (region, parameterNames, opts) => {
    var _a;
    const client = new client_ssm_1.SSMClient({ region });
    const command = new client_ssm_1.GetParametersCommand({
        Names: parameterNames,
        WithDecryption: opts === null || opts === void 0 ? void 0 : opts.withDecryption,
    });
    const response = await client.send(command);
    // eslint-disable-next-line eqeqeq
    (0, AssertHelper_1.assert)(!!(((_a = response.Parameters) === null || _a === void 0 ? void 0 : _a.length) == parameterNames.length), {
        message: `Expected to find parameter values at "${parameterNames}".`,
    });
    return response.Parameters;
};
exports.getParameters = getParameters;
/*
 * Gets Cognito credentials from SSM.
 *
 * SSM is used as a cache for credentials. The source of truth is the Cognito App Client.
 * But Cognito API has a 15/s requests rate limit.
 */
const getCredentialsFromSSM = async (region, clientIdParameterName, clientSecretParameterName) => {
    const [clientId, clientSecret] = await (0, exports.getParameters)(region, [clientIdParameterName, clientSecretParameterName], {
        withDecryption: true,
    });
    (0, AssertHelper_1.assert)(!!clientId.Value, {
        message: `Expected "${clientIdParameterName}" parameter to have a 'Value'.`,
    });
    (0, AssertHelper_1.assert)(!!clientSecret.Value, {
        message: `Expected "${clientSecretParameterName}" parameter to have a 'Value'.`,
    });
    return {
        clientId: clientId.Value,
        clientSecret: clientSecret.Value,
    };
};
exports.getCredentialsFromSSM = getCredentialsFromSSM;
/*
 * https://docs.aws.amazon.com/cognito/latest/developerguide/token-endpoint.html
 */
const getNewAccessToken = async (tokenEndpoint, clientId, clientSecret) => {
    return await axios_1.default.post(`${tokenEndpoint}`, {}, {
        params: {
            grant_type: "client_credentials",
            client_id: clientId,
            client_secret: clientSecret,
        },
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
    });
};
exports.getNewAccessToken = getNewAccessToken;
/**
 * Threshold for to trigger background refresh of auth token
 * Since expiration is only checked on request, we want to refresh the token if it is close to expiration.
 * 5 minutes is a large enough window that it is likely one request will come in before the token expires.
 * AWS cognito access tokens expire after 1 hour unless configured differently at the app client level.
 */
const EXPIRING_SOON_THRESHOLD = 300; // 5 minutes
/**
 * Determine the expiration status of a token. This is used to determine if a token refresh is required and whether or not it should be synchronous.
 */
const getTokenExpirationStatus = (decodedToken) => {
    const { exp } = decodedToken;
    if (exp === undefined) {
        return TokenExpirationStatus.EXPIRED;
    }
    // Date.now() is in millis, so we need to covert it to seconds
    const nowEpochSeconds = Date.now() / 1e3;
    // If it's not expired or about to expire (300 seconds), just return it.
    const secondsUntilExpiration = exp - nowEpochSeconds;
    if (secondsUntilExpiration <= 0) {
        return TokenExpirationStatus.EXPIRED;
    }
    if (secondsUntilExpiration < EXPIRING_SOON_THRESHOLD) {
        return TokenExpirationStatus.EXPIRING_SOON;
    }
    return TokenExpirationStatus.NOT_EXPIRED;
};
/**
 * Retrieves a access token from Cognito.
 *
 * Token is stored in memory to be reused in subsequent calls.
 * If this function is called when the token is expiring within 5 minutes, it will trigger a background refresh of the token
 * If the token has expired, the token will be refreshed synchronously.
 * This can be used as an Axios interceptor.

 * Example usage:

```
let tokenRefresh = token(
  "/warehouse-service/COGNITO_USER_ID",
  "/warehouse-service/COGNITO_SECRET",
  "https://deliverr-service-staging.auth.us-east-1.amazoncognito.com",
  "",
  "",
);
axios.interceptors.use(async (config: AxiosRequestConfig) => {
  config.headers.Authorization = `Bearer ${await tokenRefresh()}`
  return config;
});
```

 */
const token = (region, clientIdParameterName, clientSecretParameterName, userPoolDomain, clientId, clientSecret) => {
    let token;
    return async () => {
        const decoded = jsonwebtoken_1.default.decode(token);
        const tokenExpirationStatus = decoded && typeof decoded !== "string" ? getTokenExpirationStatus(decoded) : TokenExpirationStatus.EXPIRED;
        if (tokenExpirationStatus === TokenExpirationStatus.NOT_EXPIRED) {
            return token;
        }
        // We still have ~5 minutes on the current token. Refresh in background to reduce latency.
        const p = (0, exports.getNewAccessToken)(userPoolDomain, clientId, clientSecret)
            .then((response) => {
            token = response.data.access_token;
        })
            .catch(async () => {
            // If the /refresh/ failed, then it might mean that the client has been rotated.
            // If either of these fail... tough luck.
            await (0, exports.getCredentialsFromSSM)(region, clientIdParameterName, clientSecretParameterName).then(async (credentials) => {
                clientId = credentials.clientId;
                clientSecret = credentials.clientSecret;
                await (0, exports.getNewAccessToken)(userPoolDomain, clientId, clientSecret).then((response) => {
                    token = response.data.access_token;
                });
            });
        });
        // If this is the first request or the token expired without a chance to refresh async, we _must_ refresh synchronously.
        if (tokenExpirationStatus === TokenExpirationStatus.EXPIRED) {
            await p;
        }
        return token;
    };
};
exports.token = token;
exports.exportedForTesting = { getTokenExpirationStatus, TokenExpirationStatus };
//# sourceMappingURL=ClientAuth.js.map